import { useRef, useState } from "react";
import { FileSpreadsheet, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const MD_PAGE = "https://health.maryland.gov/mmcp/provider/Pages/sanctioned_list.aspx";

export const MdListUpload = ({ current, canRun, onUploaded }) => {
  const fileInput = useRef(null);
  const [asOf, setAsOf] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);

  async function upload(event) {
    event.preventDefault();
    const file = fileInput.current?.files?.[0];
    if (!file) { setError("Choose the monthly .xlsx file first"); return; }
    if (!asOf) { setError("Enter the list's “as of” date from the Maryland page"); return; }
    const body = new FormData();
    body.append("file", file);
    body.append("as_of", asOf);
    setBusy(true);
    setError("");
    try {
      const response = await api.post("/compliance/exclusions/md-list", body, { headers: { "Content-Type": "multipart/form-data" } });
      setSummary(response.data);
      if (fileInput.current) fileInput.current.value = "";
      await onUploaded();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="md-list-upload" data-testid="md-list-upload">
      <div>
        <b><FileSpreadsheet size={14} /> Maryland Medicaid Sanctioned Provider List</b>
        <p>
          Download the monthly .xlsx from <a href={MD_PAGE} target="_blank" rel="noreferrer">health.maryland.gov</a>, note the “as of” date shown next to the link, and upload it here. Every active person is matched by name plus NPI or license number. Sanction type <b>LB</b> is flagged “Verify license with MD board”, not as an exclusion.
        </p>
        <small data-testid="md-list-current">
          {current ? `Current list: as of ${current.as_of} · ${current.row_count} rows · uploaded ${new Date(current.uploaded_at).toLocaleDateString()} by ${current.uploaded_by_name}` : "No Maryland list uploaded yet."}
        </small>
      </div>
      {canRun && (
        <form className="md-list-form" onSubmit={upload} data-testid="md-list-form">
          <label>List “as of” date<input type="date" value={asOf} data-testid="md-list-as-of" onChange={(e) => setAsOf(e.target.value)} /></label>
          <label>Monthly .xlsx<input ref={fileInput} type="file" accept=".xlsx" data-testid="md-list-file" /></label>
          <button type="submit" className="button button-primary" data-testid="md-list-submit" disabled={busy}><Upload size={14} /> {busy ? "Screening…" : "Upload and screen"}</button>
        </form>
      )}
      {error && <small className="form-message error-message" data-testid="md-list-error">{error}</small>}
      {summary && (
        <p className="form-message success-message" data-testid="md-list-summary">
          List as of {summary.as_of} ({summary.row_count} rows): {summary.people_checked} people checked · {summary.md_matches} match(es) · {summary.md_verify_license} “verify license with MD board”.
        </p>
      )}
    </div>
  );
};
